import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, GripVertical, Loader2 } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

/**
 * Reorderable, replaceable image manager for a single product.
 * Props: productId, images (array of URLs), onChange(images)
 */
export default function ProductImageManager({ productId, images, onChange }) {
  const [items, setItems] = useState(images || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(images || []); }, [images, productId]);

  const dirty = JSON.stringify(items) !== JSON.stringify(images || []);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("folder", "products");
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      setItems((cur) => [...cur, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} file(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const arr = [...items];
    const [moved] = arr.splice(result.source.index, 1);
    arr.splice(result.destination.index, 0, moved);
    setItems(arr);
  };

  const remove = (idx) => setItems((cur) => cur.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${productId}/images`, { images: items });
      toast.success("Product images updated");
      onChange?.(items);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label
          data-testid="img-upload-input"
          className="btn-secondary inline-flex items-center gap-2 cursor-pointer"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Add photos"}
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
            disabled={uploading}
          />
        </label>
        <div className="text-xs opacity-60">Drag to reorder · First image is the card thumbnail</div>
        {dirty && (
          <button
            data-testid="img-save-order"
            onClick={save}
            disabled={saving}
            className="btn-primary ml-auto text-sm px-5 py-2"
          >
            {saving ? "Saving…" : "Save order"}
          </button>
        )}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="imgs" direction="horizontal" isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3"
            >
              {items.map((src, i) => (
                <Draggable key={`${src}-${i}`} draggableId={`${src}-${i}`} index={i}>
                  {(prov, snap) => (
                    <div
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      className={`relative group image-soft aspect-square overflow-hidden ${snap.isDragging ? "ring-2 ring-offset-2" : ""}`}
                      style={{
                        ...prov.draggableProps.style,
                        background: "var(--kaiya-bg-2)",
                      }}
                      data-testid={`img-item-${i}`}
                    >
                      <img src={src.startsWith("/api/") ? `${api.defaults.baseURL.replace("/api","")}${src}` : src} alt="" className="w-full h-full object-cover" />
                      <div
                        {...prov.dragHandleProps}
                        className="absolute top-2 left-2 p-1.5 rounded backdrop-blur-md cursor-grab active:cursor-grabbing"
                        style={{ background: "rgba(249,246,240,0.85)" }}
                      >
                        <GripVertical size={14} />
                      </div>
                      <button
                        onClick={() => remove(i)}
                        data-testid={`img-remove-${i}`}
                        aria-label="Remove"
                        className="absolute top-2 right-2 p-1.5 rounded-full text-white"
                        style={{ background: "var(--kaiya-error)" }}
                      >
                        <X size={12} />
                      </button>
                      {i === 0 && (
                        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest text-white" style={{ background: "var(--kaiya-primary)" }}>
                          Cover
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {items.length === 0 && (
        <div className="text-sm opacity-60 border rounded-2xl p-8 text-center" style={{ borderColor: "var(--kaiya-border)" }}>
          No images yet. Click <strong>Add photos</strong> to upload.
        </div>
      )}
    </div>
  );
}
